// Based on Context7 documentation for React Three Fiber v8.x and Three.js r160

import React, { useRef, useMemo, useEffect } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { OrbitControls, Stats, PerformanceMonitor } from '@react-three/drei'
import * as THREE from 'three'
import { useSimulationStore } from '../store/simulationStore'

// Particle system using instanced mesh for performance
function ParticleField({ count = 10000 }) {
  const mesh = useRef<THREE.InstancedMesh>(null)
  const { particleSize, particleColor, gravityStrength } = useSimulationStore()
  
  // Initialize particle positions and velocities
  const particles = useMemo(() => {
    const temp = []
    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2
      const r = Math.random() * 50 + 10
      const y = (Math.random() - 0.5) * 10
      
      temp.push({
        position: new THREE.Vector3(
          r * Math.cos(theta),
          y,
          r * Math.sin(theta)
        ),
        velocity: new THREE.Vector3(
          Math.random() - 0.5,
          0,
          Math.random() - 0.5
        ).multiplyScalar(0.1)
      })
    }
    return temp
  }, [count])

  // Set up instanced mesh matrices
  useEffect(() => {
    if (!mesh.current) return
    
    const matrix = new THREE.Matrix4()
    particles.forEach((particle, i) => {
      matrix.setPosition(particle.position)
      mesh.current!.setMatrixAt(i, matrix)
    })
    mesh.current.instanceMatrix.needsUpdate = true
  }, [particles])

  // Animation loop with physics
  useFrame((state, delta) => {
    if (!mesh.current) return
    
    const matrix = new THREE.Matrix4()
    const center = new THREE.Vector3(0, 0, 0)
    
    particles.forEach((particle, i) => {
      // Apply gravitational force
      const force = center.clone()
        .sub(particle.position)
        .normalize()
        .multiplyScalar(gravityStrength * delta)
      
      particle.velocity.add(force)
      particle.position.add(particle.velocity.clone().multiplyScalar(delta * 60))
      
      // Update instance matrix
      matrix.setPosition(particle.position)
      mesh.current!.setMatrixAt(i, matrix)
    })
    
    mesh.current.instanceMatrix.needsUpdate = true
  })

  return (
    <instancedMesh ref={mesh} args={[undefined, undefined, count]}>
      <sphereGeometry args={[particleSize, 8, 6]} />
      <meshBasicMaterial color={particleColor} />
    </instancedMesh>
  )
}

// Main galaxy scene component
export function GalaxyScene() {
  const { adaptPerformance } = useSimulationStore()
  
  return (
    <Canvas
      camera={{ position: [0, 30, 100], fov: 60 }}
      gl={{ 
        antialias: true,
        alpha: true,
        powerPreference: 'high-performance'
      }}
    >
      {/* Performance monitoring from drei */}
      <PerformanceMonitor
        onDecline={() => adaptPerformance('decrease')}
        onIncline={() => adaptPerformance('increase')}
      />
      
      {/* Lighting */}
      <ambientLight intensity={0.5} />
      <pointLight position={[0, 0, 0]} intensity={1} />
      
      {/* Particle system */}
      <ParticleField count={10000} />
      
      {/* Controls */}
      <OrbitControls 
        enableDamping
        dampingFactor={0.05}
        minDistance={10}
        maxDistance={200}
      />
      
      {/* Performance stats */}
      <Stats />
    </Canvas>
  )
}